/**
 * Client-side error reporter.
 *
 * POSTs a compact error payload to `/api/client-error`, which logs a
 * `[client-error]` marker to the server logs (the same console-marker pattern
 * as `app/api/_lib/lead-sink.ts`). Until now the booking flow had NO client
 * error telemetry — Microsoft Clarity flagged a handful of sessions with zero
 * stack, so we were effectively blind to render/hydration crashes.
 *
 * Uses `navigator.sendBeacon` when available so the report survives an unload /
 * navigation (a crashing page often unloads immediately), falling back to
 * `fetch` with `keepalive`. Best-effort and self-silencing — reporting must
 * never throw or it could mask/aggravate the very error it is reporting.
 */
export type ClientErrorContext = {
  /** Where the error was caught: "angebot", "global", "window.onerror", … */
  boundary?: string;
  [key: string]: unknown;
};

export function reportClientError(
  error: unknown,
  context: ClientErrorContext = {},
): void {
  try {
    const payload = {
      message:
        error instanceof Error ? error.message : String(error ?? "unknown"),
      stack: error instanceof Error ? error.stack?.slice(0, 4000) : undefined,
      digest:
        error && typeof error === "object" && "digest" in error
          ? String((error as { digest?: unknown }).digest ?? "")
          : undefined,
      url: typeof location !== "undefined" ? location.href : undefined,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      ...context,
    };
    const body = JSON.stringify(payload);

    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      navigator.sendBeacon(
        "/api/client-error",
        new Blob([body], { type: "application/json" }),
      );
      return;
    }

    void fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Telemetry must never throw.
  }
}
